import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, ExternalLink, Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { CarrierMatchingEngine } from '@/components/CarrierMatchingEngine';
import { AFFILIATE_CARRIERS } from '@/data/affiliateCarriers';

interface QuestionnaireData {
  name: string;
  age: string;
  state: string;
  coverageAmount: string;
  smokingStatus?: string;
  policyType?: string;
  healthStatus?: string;
  majorHealthConditions?: string;
  medications?: string;
  familyHistory?: string;
  riskyHobbies?: string;
  highRiskOccupation?: string;
  alcoholConsumption?: string;
  exercise?: string;
  priority?: string;
  noMedicalExam?: string;
  approvalPreference?: string;
}

interface QuestionaireSummaryProps {
  data: QuestionnaireData;
  recommendation: {
    recommendedCompany: string;
    referralLink: string;
    description: string;
    score: number;
    reasons: string[];
    carrierId?: string;
  };
  onGetQuote: () => void;
  onStartOver: () => void;
}

const handleAffiliateClick = async (url: string, carrierName: string) => {
  try {
    // Track referral click
    await supabase.from('referral_logs').insert({
      carrier_name: carrierName,
      referral_url: url,
      session_id: sessionStorage.getItem('session_id') || 'anonymous',
      user_agent: navigator.userAgent,
      ip_address: '', // Will be set by server if needed
      clicked_at: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error tracking referral click:', error);
  }
  
  // Open affiliate link in new tab
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const QuestionaireSummary: React.FC<QuestionaireSummaryProps> = ({
  data,
  recommendation,
  onGetQuote,
  onStartOver
}) => {
  const formatAnswer = (value: string | undefined, type: 'yesno' | 'text' = 'text') => {
    if (!value) return 'Not specified';
    if (type === 'yesno') {
      return value === 'yes' ? 'Yes' : value === 'no' ? 'No' : value;
    }
    return value;
  };

  // Get carrier details and compute match percentage
  const carrierId = recommendation.carrierId || 'ladder-life';
  const carrier = AFFILIATE_CARRIERS.find(c => c.id === carrierId);
  const isLadder = carrierId === 'ladder-life';
  const matchPercent = CarrierMatchingEngine.computeMatchPercent(recommendation.score, carrierId);
  
  // Get Ladder for secondary recommendation if current carrier isn't Ladder
  const ladderCarrier = AFFILIATE_CARRIERS.find(c => c.id === 'ladder-life');

  return (
    <div className="flex justify-center">
      <div className="w-full max-w-2xl space-y-6">
        {/* Header */}
        <div className="text-center">
          <CheckCircle className="h-16 w-16 text-primary mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Your Quote is Ready!
          </h1>
          <p className="text-xl text-muted-foreground">
            {isLadder ? 'Based on your answers, we found the perfect match' : 'Here\'s your personalized recommendation'}
          </p>
        </div>

        {/* Recommendation Card */}
        <Card className="shadow-lg border-0" style={{ boxShadow: 'var(--card-shadow)' }}>
          {isLadder && (
            <div className="bg-gradient-to-r from-primary to-primary-foreground/90 text-white px-4 py-2 rounded-t-lg">
              <div className="flex items-center justify-center gap-2">
                <Star className="h-4 w-4 fill-current" />
                <span className="font-semibold text-sm">Safurra Recommends</span>
                <Star className="h-4 w-4 fill-current" />
              </div>
            </div>
          )}
          <CardHeader className={isLadder ? "bg-primary text-primary-foreground rounded-t-none" : "bg-primary text-primary-foreground"}>
            <CardTitle className="text-xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                {carrier?.logo && (
                  <img 
                    src={carrier.logo} 
                    alt={`${recommendation.recommendedCompany} logo`}
                    className="h-10 w-auto object-contain bg-white/10 rounded p-1"
                  />
                )}
                <span>Recommended: {recommendation.recommendedCompany}</span>
              </div>
              <span className="text-sm bg-primary-foreground/20 px-2 py-1 rounded">
                {matchPercent}% Match
              </span>
            </CardTitle>
            {isLadder && (
              <p className="text-primary-foreground/90 text-sm font-medium">
                Best blend of price, speed, and flexibility for your profile
              </p>
            )}
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-muted-foreground mb-4">
              {recommendation.description}
            </p>
            
            <div className="mb-6">
              <h4 className="font-semibold mb-2">
                {isLadder ? 'Why Ladder Life is perfect for you:' : 'Why this carrier is perfect for you:'}
              </h4>
              <ul className="space-y-1">
                {(isLadder && carrier?.talkingPoints ? carrier.talkingPoints : recommendation.reasons).map((point, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm">{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {recommendation.referralLink && (
              <Button 
                onClick={() => handleAffiliateClick(recommendation.referralLink, recommendation.recommendedCompany)}
                className="w-full mb-4"
                variant="hero"
                size="lg"
              >
                See My Quote
                <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            )}

            {!isLadder && ladderCarrier && ladderCarrier.referralLink && (
              <Button 
                onClick={() => handleAffiliateClick(ladderCarrier.referralLink, ladderCarrier.name)}
                className="w-full"
                variant="outline"
                size="lg"
              >
                Compare with Safurra Recommended
                <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            )}

            {/* Compliance footnote */}
            <p className="text-xs text-muted-foreground mt-4 text-center">
              Safurra may earn a commission from partner links. We recommend options we believe match your preferences.
            </p>
          </CardContent>
        </Card>

        {/* Summary Card */}
        <Card className="shadow-lg border-0" style={{ boxShadow: 'var(--card-shadow)' }}>
          <CardHeader>
            <CardTitle>Your Information Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-foreground">Basic Information</h4>
                <div className="text-sm text-muted-foreground space-y-1 mt-2">
                  <div>Name: {data.name}</div>
                  <div>Age: {data.age}</div>
                  <div>State: {data.state}</div>
                  <div>Coverage: {data.coverageAmount}</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-foreground">Health & Lifestyle</h4>
                <div className="text-sm text-muted-foreground space-y-1 mt-2">
                  <div>Smoking: {formatAnswer(data.smokingStatus, 'yesno')}</div>
                  <div>Health Status: {formatAnswer(data.healthStatus)}</div>
                  <div>Exercise: {formatAnswer(data.exercise)}</div>
                  <div>Major Health Conditions: {formatAnswer(data.majorHealthConditions, 'yesno')}</div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-foreground">Policy Preferences</h4>
                <div className="text-sm text-muted-foreground space-y-1 mt-2">
                  <div>Policy Type: {formatAnswer(data.policyType)}</div>
                  <div>Priority: {formatAnswer(data.priority)}</div>
                  <div>No Medical Exam: {formatAnswer(data.noMedicalExam, 'yesno')}</div>
                  <div>Approval Speed: {formatAnswer(data.approvalPreference)}</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-foreground">Risk Factors</h4>
                <div className="text-sm text-muted-foreground space-y-1 mt-2">
                  <div>Risky Hobbies: {formatAnswer(data.riskyHobbies, 'yesno')}</div>
                  <div>High Risk Job: {formatAnswer(data.highRiskOccupation, 'yesno')}</div>
                  <div>Family History: {formatAnswer(data.familyHistory, 'yesno')}</div>
                  <div>Alcohol Use: {formatAnswer(data.alcoholConsumption)}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-4">
          <Button 
            variant="outline"
            onClick={onStartOver}
            className="flex-1"
          >
            Get Another Quote
          </Button>
        </div>
      </div>
    </div>
  );
};